<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import InputError from "@/Components/InputError.vue";
import InputLabel from "@/Components/InputLabel.vue";
import FormSection from "@/Components/FormSection.vue";
import TextInput from "@/Components/TextInput.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";

import { useForm } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { ref, watch } from "vue";

const props = defineProps({
  cover: Object,
  returnTo: { type: String, default: "" },
  readOnly: { type: Boolean, default: false },
});

const saving = ref(false);

const coverImageInputRef = ref(null);
const clientLogoInputRef = ref(null);

const tempCoverImage = ref(null);
const tempClientLogo = ref(null);

const form = useForm({
  _method: "POST",

  title: props.cover?.title || "",
  subtitle: props.cover?.subtitle || "",
  paragraph: props.cover?.paragraph || "",
  cover_date: props.cover?.cover_date || "",
  status: props.cover?.status ?? true,

  cover_image: null,
  client_logo: null,
  remove_cover_image: false,
  remove_client_logo: false,
  return_to: props.returnTo || route("sales.lookbook.index"),
});

const revokeBlob = (url) => {
  if (!url || !url.startsWith("blob:")) return;
  try {
    URL.revokeObjectURL(url);
  } catch (e) {}
};

watch(
  () => tempCoverImage.value?.url,
  (next, prev) => {
    if (prev && prev !== next) revokeBlob(prev);
  }
);

watch(
  () => tempClientLogo.value?.url,
  (next, prev) => {
    if (prev && prev !== next) revokeBlob(prev);
  }
);

const resetCoverImageInput = () => {
  form.cover_image = null;
  form.remove_cover_image = false;
  tempCoverImage.value = null;
  if (coverImageInputRef.value) coverImageInputRef.value.value = "";
};

const resetClientLogoInput = () => {
  form.client_logo = null;
  form.remove_client_logo = false;
  tempClientLogo.value = null;
  if (clientLogoInputRef.value) clientLogoInputRef.value.value = "";
};

const onCoverImageSelected = (e) => {
  const file = (e.target.files || [])[0] || null;
  form.cover_image = file;

  if (file) form.remove_cover_image = false;

  tempCoverImage.value = file ? { name: file.name, url: URL.createObjectURL(file) } : null;

  if (file) form.clearErrors("cover_image");
};

const onClientLogoSelected = (e) => {
  const file = (e.target.files || [])[0] || null;
  form.client_logo = file;

  if (file) form.remove_client_logo = false;

  tempClientLogo.value = file ? { name: file.name, url: URL.createObjectURL(file) } : null;

  if (file) form.clearErrors("client_logo");
};

const markRemoveCurrentCoverImage = () => {
  form.remove_cover_image = true;
  form.cover_image = null;
  tempCoverImage.value = null;
  if (coverImageInputRef.value) coverImageInputRef.value.value = "";
  form.clearErrors("cover_image");
};

const markRemoveCurrentClientLogo = () => {
  form.remove_client_logo = true;
  form.client_logo = null;
  tempClientLogo.value = null;
  if (clientLogoInputRef.value) clientLogoInputRef.value.value = "";
  form.clearErrors("client_logo");
};

const undoRemoveCurrentCoverImage = () => {
  form.remove_cover_image = false;
};

const undoRemoveCurrentClientLogo = () => {
  form.remove_client_logo = false;
};

const goBack = () => {
  Inertia.get(form.return_to || route("sales.lookbook.index"), {}, { preserveState: true, preserveScroll: true });
};

const validateAndScroll = () => {
  form.clearErrors("title");

  const title = (form.title || "").toString().trim();
  if (!title) {
    form.setError("title", "Title is required.");
    document.getElementById("title")?.scrollIntoView({ behavior: "smooth", block: "center" });
    return false;
  }

  return true;
};

const saveForm = () => {
  if (!validateAndScroll()) return;

  saving.value = true;

  const isEdit = !!props.cover?.id;

  const endpoint = isEdit
    ? route("sales.lookbook.covers.update", { lookbookCover: props.cover.id })
    : route("sales.lookbook.covers.store");

  const prevMethod = form._method;
  form._method = isEdit ? "PUT" : "POST";

  form.post(endpoint, {
    preserveScroll: true,
    forceFormData: true,
    onFinish() {
      form._method = prevMethod;
      saving.value = false;
    },
  });
};
</script>

<template>
  <AppLayout title="Lookbook Cover">
    <template #title>
      <a href="#" @click.prevent="goBack">
        <SecondaryButton>Back</SecondaryButton>
      </a>
    </template>

    <div class="max-w-7xl mx-auto py-5 sm:px-6 lg:px-8">
      <FormSection>
        <template #title>
          <a href="#" @click.prevent="goBack">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>

        <template #form>
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Title" :required="true" />
            <TextInput id="title" v-model="form.title" type="text" class="mt-1 block w-full" :disabled="readOnly" />
            <InputError :message="form.errors.title" class="mt-2" />
          </div>

          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Subtitle" />
            <TextInput
              id="subtitle"
              v-model="form.subtitle"
              type="text"
              class="mt-1 block w-full"
              :disabled="readOnly"
            />
            <InputError :message="form.errors.subtitle" class="mt-2" />
          </div>
          
          <div class="col-span-6 sm:col-span-4">
            <InputLabel value="Paragraph" />
            <textarea
            rows="8"
            id="paragraph"
            v-model="form.paragraph"
            class="border-gray-300 focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 rounded-md shadow-sm mt-1 block w-full disabled:bg-gray-50"
            :disabled="readOnly"
            />
            <p class="mt-2 text-xs text-gray-500">
              The security disclaimer is added automatically at the bottom of the cover.
            </p>
            <InputError :message="form.errors.paragraph" class="mt-2" />
          </div>
          
        </template>
      </FormSection>

      <br />

      <FormSection :noGrid="true">
        <template #title>Cover Image</template>

        <template #form>
          <div v-if="cover?.id" class="space-y-3">
            <div class="flex items-start gap-4">
              <div class="w-36">
                <img
                  v-if="cover.cover_image_url && !form.remove_cover_image"
                  :src="cover.cover_image_url"
                  class="h-28 w-28 object-cover rounded border"
                  :alt="cover.cover_image_file_name"
                />
                <div v-else class="h-28 w-28 rounded border flex items-center justify-center text-gray-400">—</div>
              </div>

              <div class="flex-1 space-y-2">
                <div class="text-sm text-gray-700">
                  <span class="font-semibold">Current:</span>
                  <span v-if="cover.cover_image_file_name && !form.remove_cover_image">{{ cover.cover_image_file_name }}</span>
                  <span v-else class="text-gray-500">No image</span>
                </div>

                <div class="flex gap-2">
                  <button
                    type="button"
                    class="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    @click="markRemoveCurrentCoverImage"
                    :disabled="readOnly || !cover.cover_image_file_name || form.remove_cover_image"
                    title="Remove current image"
                  >
                    Remove image
                  </button>

                  <button
                    v-if="form.remove_cover_image"
                    type="button"
                    class="px-3 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    @click="undoRemoveCurrentCoverImage"
                    :disabled="readOnly"
                    title="Undo remove"
                  >
                    Undo
                  </button>
                </div>

                <p v-if="form.remove_cover_image" class="text-sm text-amber-700">
                  Image will be removed when you click <b>Save</b>.
                </p>
              </div>
            </div>
          </div>

          <div class="space-y-3" :class="cover?.id ? 'mt-6' : ''">
            <InputLabel
              :value="cover?.id ? 'Replace image (only 1)' : 'Upload image (only 1)'"
              :required="false"
            />

            <div class="flex justify-center items-center w-full">
              <label
                for="cover-image-input"
                class="flex flex-col justify-center items-center w-full h-32 rounded-lg border-2 border-dashed cursor-pointer hover:bg-gray-100 border-gray-300"
              >
                <div class="flex flex-col justify-center items-center pt-5 pb-6">
                  <p class="mb-2 text-sm text-gray-500">
                    <span class="font-semibold">Click to upload</span> (1 image)
                  </p>
                  <p class="text-xs text-gray-500">Max. File Size: 10MB</p>
                </div>

                <input
                  ref="coverImageInputRef"
                  id="cover-image-input"
                  type="file"
                  accept="image/*"
                  @change="onCoverImageSelected"
                  class="hidden"
                  :disabled="readOnly"
                />
              </label>
            </div>

            <div class="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4" v-if="tempCoverImage">
              <div class="relative rounded-lg p-2 bg-white shadow-sm">
                <img :src="tempCoverImage.url" alt="" class="w-full h-32 object-cover rounded-md" />
                <div class="mt-1 text-xs truncate text-center">{{ tempCoverImage.name }}</div>
                <button
                  type="button"
                  class="absolute top-1 right-1 bg-white/80 rounded-full p-1 hover:bg-white"
                  @click="resetCoverImageInput"
                  title="Remove selected image"
                >
                  <span class="fa fa-trash text-red-600 text-sm"></span>
                </button>
              </div>
            </div>

            <InputError :message="form.errors.cover_image" class="mt-2" />
          </div>
        </template>
      </FormSection>

      <br />

      <FormSection :noGrid="true">
        <template #title>Client Logo</template>

        <template #form>
          <div v-if="cover?.id" class="space-y-3">
            <div class="flex items-start gap-4">
              <div class="w-36">
                <img
                  v-if="cover.client_logo_url && !form.remove_client_logo"
                  :src="cover.client_logo_url"
                  class="h-28 w-28 object-contain rounded border"
                  :alt="cover.client_logo_file_name"
                />
                <div v-else class="h-28 w-28 rounded border flex items-center justify-center text-gray-400">—</div>
              </div>

              <div class="flex-1 space-y-2">
                <div class="text-sm text-gray-700">
                  <span class="font-semibold">Current:</span>
                  <span v-if="cover.client_logo_file_name && !form.remove_client_logo">{{ cover.client_logo_file_name }}</span>
                  <span v-else class="text-gray-500">No logo</span>
                </div>

                <div class="flex gap-2">
                  <button
                    type="button"
                    class="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                    @click="markRemoveCurrentClientLogo"
                    :disabled="readOnly || !cover.client_logo_file_name || form.remove_client_logo"
                    title="Remove current logo"
                  >
                    Remove logo
                  </button>

                  <button
                    v-if="form.remove_client_logo"
                    type="button"
                    class="px-3 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    @click="undoRemoveCurrentClientLogo"
                    :disabled="readOnly"
                    title="Undo remove"
                  >
                    Undo
                  </button>
                </div>

                <p v-if="form.remove_client_logo" class="text-sm text-amber-700">
                  Logo will be removed when you click <b>Save</b>.
                </p>
              </div>
            </div>
          </div>

          <div class="space-y-3" :class="cover?.id ? 'mt-6' : ''">
            <InputLabel :value="cover?.id ? 'Replace logo (only 1)' : 'Upload logo (only 1)'" />

            <div class="flex justify-center items-center w-full">
              <label
                for="client-logo-input"
                class="flex flex-col justify-center items-center w-full h-32 rounded-lg border-2 border-dashed cursor-pointer hover:bg-gray-100 border-gray-300"
              >
                <div class="flex flex-col justify-center items-center pt-5 pb-6">
                  <p class="mb-2 text-sm text-gray-500">
                    <span class="font-semibold">Click to upload</span> (1 image)
                  </p>
                  <p class="text-xs text-gray-500">Max. File Size: 10MB</p>
                </div>

                <input
                  ref="clientLogoInputRef"
                  id="client-logo-input"
                  type="file"
                  accept="image/*"
                  @change="onClientLogoSelected"
                  class="hidden"
                  :disabled="readOnly"
                />
              </label>
            </div>

            <div class="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4" v-if="tempClientLogo">
              <div class="relative rounded-lg p-2 bg-white shadow-sm">
                <img :src="tempClientLogo.url" alt="" class="w-full h-32 object-contain rounded-md" />
                <div class="mt-1 text-xs truncate text-center">{{ tempClientLogo.name }}</div>
                <button
                  type="button"
                  class="absolute top-1 right-1 bg-white/80 rounded-full p-1 hover:bg-white"
                  @click="resetClientLogoInput"
                  title="Remove selected image"
                >
                  <span class="fa fa-trash text-red-600 text-sm"></span>
                </button>
              </div>
            </div>

            <InputError :message="form.errors.client_logo" class="mt-2" />
          </div>
        </template>
      </FormSection>

      <br />

      <FormSection :noGrid="true">
        <template #actions>
          <a href="#" class="mr-5" v-if="!saving" @click.prevent="goBack">
            <SecondaryButton type="button">Back</SecondaryButton>
          </a>

          <PrimaryButton type="button" @click.prevent="saveForm" :disabled="saving || readOnly">
            Save
          </PrimaryButton>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
